import { lazy, Suspense } from "react";
import type { FlatSection } from "./types";
import { KpiRow } from "./sections/KpiRow";
import { SectionList } from "./sections/SectionList";
import { SectionMarkdown } from "./sections/SectionMarkdown";
import { SectionTable } from "./sections/SectionTable";
import { SectionCallout } from "./sections/SectionCallout";
import { SectionBullet } from "./sections/SectionBullet";
import { SectionBarList } from "./sections/SectionBarList";
import { SectionTracker } from "./sections/SectionTracker";
import { TierBar } from "./sections/TierBar";
import { StoreProgress } from "./sections/StoreProgress";
import { FilterTable } from "./sections/FilterTable";
import { SectionSkeleton } from "./SectionSkeleton";
import { FlatPanelNotes } from "./NotesAffordance";

// All three Recharts-heavy sections lazy-load — concise-first default
// ships zero chart code (R8 lock).
const LazySectionChart = lazy(() =>
  import("./sections/SectionChart").then((m) => ({ default: m.SectionChart })),
);
const LazySectionStatTable = lazy(() =>
  import("./sections/SectionStatTable").then((m) => ({ default: m.SectionStatTable })),
);
const LazySectionSparkline = lazy(() =>
  import("./sections/SectionSparkline").then((m) => ({ default: m.SectionSparkline })),
);

/**
 * Dispatcher for non-recursive section kinds. Used inside tab_group tabs and
 * row_detail slide-overs (both forbid nested tab_group; answer is dashboard
 * level only). SectionChart is code-split — Recharts is heavy and stays out of
 * the concise-first default view until a chart actually needs to render.
 */
export function FlatSectionDispatch({ section }: { section: FlatSection }) {
  // Flat contexts (tab_group tabs, row_detail) render without a
  // SectionShell/title bar, so notes-bearing panels get a minimal title row +
  // NOTES pill via FlatPanelNotes (spec §3.3: in-tab titles display when a
  // panel carries notes). Notes-free panels render exactly as before.
  const notes = "notes" in section ? section.notes : undefined;
  if (notes) {
    return (
      <FlatPanelNotes title={"title" in section ? section.title : undefined} notes={notes}>
        <FlatSectionBody section={section} />
      </FlatPanelNotes>
    );
  }
  return <FlatSectionBody section={section} />;
}

function FlatSectionBody({ section }: { section: FlatSection }) {
  switch (section.kind) {
    case "kpi_row":
      return <KpiRow items={section.items} />;
    case "table":
      return (
        <SectionTable
          title={section.title}
          columns={section.columns}
          rows={section.rows}
          rowDetail={section.row_detail}
          evidencePerRow={section.evidence_per_row}
          storeNumberPerRow={section.store_number_per_row}
          head={section.head}
          noun={section.noun}
          emptyState={section.empty_state}
        />
      );
    case "chart":
      return (
        <Suspense fallback={<SectionSkeleton shape="chart" />}>
          <LazySectionChart
            title={section.title}
            chartType={section.chart_type}
            data={section.data}
            xKey={section.x_key}
            series={section.series}
          />
        </Suspense>
      );
    case "list":
      return <SectionList title={section.title} items={section.items} />;
    case "markdown":
      return <SectionMarkdown title={section.title} body={section.body} />;
    case "callout":
      return (
        <SectionCallout severity={section.severity} body={section.body} title={section.title} />
      );
    case "stat_table":
      return (
        <Suspense fallback={<SectionSkeleton shape="table" />}>
          <LazySectionStatTable
            title={section.title}
            columns={section.columns}
            rows={section.rows}
            evidencePerRow={section.evidence_per_row}
          />
        </Suspense>
      );
    case "bullet":
      return (
        <SectionBullet
          title={section.title}
          value={section.value}
          target={section.target}
          bands={section.bands}
          unit={section.unit}
        />
      );
    case "sparkline":
      return (
        <Suspense fallback={<SectionSkeleton shape="chart" />}>
          <LazySectionSparkline
            title={section.title}
            data={section.data}
            xKey={section.x_key}
            yKey={section.y_key}
          />
        </Suspense>
      );
    case "bar_list":
      return (
        <SectionBarList
          title={section.title}
          items={section.items}
          head={section.head}
          noun={section.noun}
          emptyState={section.empty_state}
        />
      );
    case "tracker":
      return <SectionTracker title={section.title} cells={section.cells} />;
    case "tier_bar":
      return (
        <TierBar
          segments={section.segments}
          value={section.value}
          valueLabel={section.value_label}
          target={section.target}
          unit={section.unit}
        />
      );
    case "store_progress":
      return <StoreProgress items={section.items} target={section.target} unit={section.unit} />;
    case "filter_table":
      return (
        <FilterTable
          title={section.title}
          columns={section.columns}
          rows={section.rows}
          flags={section.flags}
          scope={section.scope}
          head={section.head}
          noun={section.noun}
          defaultSort={section.default_sort}
          emptyState={section.empty_state}
        />
      );
    default:
      return null;
  }
}
